import axios from 'axios';

/**
 * Candidate Comments — typed API client.
 *
 * Centralises calls against /api/candidate-comments/*. All methods unwrap
 * the standard `{ success, data }` envelope and throw on `success: false`.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface CandidateComment {
  id: number;
  employer_id: number;
  candidate_id: number;
  comment: string;
  created_at: string;
  updated_at: string;
}

export interface CandidateCommentSummary {
  candidate_id: number;
  comment: string;
  updated_at: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: Record<string, string>;
  message?: string;
}

async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const res = await promise;
  if (!res.data?.success) {
    const err: any = new Error(res.data?.error || 'Request failed');
    err.fieldErrors = res.data?.errors;
    throw err;
  }
  return res.data.data as T;
}

export const candidateCommentsService = {
  /**
   * Bulk-fetch summaries for a list of candidate ids. Used to power the
   * green "has comment" marker on candidate lists with a single round-trip.
   * Returns a `Record<candidateId, summary>` shape.
   */
  bulk: async (candidateIds: number[]) => {
    if (candidateIds.length === 0) {
      return {} as Record<string, CandidateCommentSummary>;
    }
    const params = new URLSearchParams();
    params.set('candidate_ids', candidateIds.join(','));
    const res = await unwrap<{ comments: Record<string, CandidateCommentSummary> }>(
      axios.get(`${API_URL}/candidate-comments?${params.toString()}`, {
        headers: authHeaders(),
      }),
    );
    return res.comments;
  },

  getOne: (candidateId: number) =>
    unwrap<{ comment: CandidateComment | null }>(
      axios.get(`${API_URL}/candidate-comments/${candidateId}`, {
        headers: authHeaders(),
      }),
    ),

  save: (candidateId: number, comment: string) =>
    unwrap<{ comment: CandidateComment }>(
      axios.put(
        `${API_URL}/candidate-comments/${candidateId}`,
        { comment },
        { headers: authHeaders() },
      ),
    ),

  remove: (candidateId: number) =>
    unwrap<{ removed: boolean; candidate_id: number }>(
      axios.delete(`${API_URL}/candidate-comments/${candidateId}`, {
        headers: authHeaders(),
      }),
    ),
};
